import React, { useState, useEffect } from 'react';
import { getAllUsers, updateUser, deleteUser, toggleUserStatus } from '../services/api';
import UserModal from '../components/Admin/UserModal';
import Toast from '../components/Toast/Toast';
import { useToast } from '../hooks/useToast';
import { useResponsive } from '../hooks/useResponsive';
import { Users, UserCheck, UserX, UserPlus, Filter, Eye, Edit, Key, Trash2, Copy, CheckCircle, XCircle, MoreVertical, Search, Calendar } from 'lucide-react';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, name, email
  const [filterPeriod, setFilterPeriod] = useState('all'); // all, today, week, month, year, custom
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [showFilters, setShowFilters] = useState(false); // Toggle filter dropdown
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { toasts, hideToast, success, error, warning } = useToast();
  const { isMobile } = useResponsive();

  useEffect(() => {
    // Check if user is admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.isAdmin) {
      error('Admin access required');
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
      return;
    }
    fetchUsers();
  }, [error]);

  const fetchUsers = async () => {
    try {
      const { data } = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        await fetchUsers();
        success('User deleted successfully');
      } catch (err) {
        error('Failed to delete user');
      }
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = prompt(`Reset password for ${user.name}?\n\nEnter new password:`);
    
    if (newPassword) {
      if (newPassword.length < 6) {
        warning('Password must be at least 6 characters long');
        return;
      }
      
      try {
        await updateUser(user.id, { 
          name: user.name,
          email: user.email,
          password: newPassword,
          is_active: user.is_active
        });
        
        success(`Password reset successfully for ${user.name}!\n\nNew Password: ${newPassword}`, 5000);
        await fetchUsers();
      } catch (err) {
        error('Failed to reset password: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await toggleUserStatus(userId, !currentStatus);
      await fetchUsers();
      success(`User status updated to ${!currentStatus ? 'Active' : 'Inactive'}`);
    } catch (err) {
      error('Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Status filter
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user.is_active) ||
                         (filterStatus === 'inactive' && !user.is_active);
    
    // Period filter
    let matchesPeriod = true;
    if (filterPeriod !== 'all') {
      const userDate = new Date(user.created_at);
      const now = new Date();
      
      if (filterPeriod === 'custom') {
        // Custom date range filter
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          matchesPeriod = userDate >= start && userDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          matchesPeriod = userDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          matchesPeriod = userDate <= end;
        }
      } else {
        switch(filterPeriod) {
          case 'today':
            matchesPeriod = userDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesPeriod = userDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesPeriod = userDate >= monthAgo;
            break;
          case 'year':
            const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            matchesPeriod = userDate >= yearAgo;
            break;
          default:
            matchesPeriod = true;
        }
      }
    }
    
    return matchesSearch && matchesStatus && matchesPeriod;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch(sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'email':
        return a.email.localeCompare(b.email);
      default:
        return 0;
    }
  });

  const getFilteredStats = () => {
    return {
      total: filteredUsers.length,
      active: filteredUsers.filter(u => u.is_active).length,
      inactive: filteredUsers.filter(u => !u.is_active).length,
      newThisMonth: filteredUsers.filter(u => {
        const userDate = new Date(u.created_at);
        const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return userDate >= monthAgo;
      }).length
    };
  };

  const stats = getFilteredStats();

  return (
    <div className="container">
      {/* Header */}
      <div style={{ marginBottom: isMobile ? '24px' : '40px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '12px' : '16px', 
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? '10px' : '12px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={isMobile ? 24 : 28} color="white" />
          </div>
          <div>
            <h1 style={{ 
              margin: 0, 
              color: '#2c3e50', 
              fontSize: isMobile ? '24px' : '32px', 
              fontWeight: '700' 
            }}>Admin Panel</h1>
            <p style={{ 
              color: '#7f8c8d', 
              margin: '4px 0 0 0', 
              fontSize: isMobile ? '13px' : '15px' 
            }}>Manage all users and their accounts</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '24px' : '40px'
      }}>
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          border: 'none',
          padding: isMobile ? '20px' : '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <Users size={isMobile ? 24 : 28} style={{ opacity: 0.9 }} />
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '6px 12px', 
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}>
              Total
            </div>
          </div>
          <div style={{ 
            fontSize: isMobile ? '36px' : '42px', 
            fontWeight: '700', 
            marginBottom: '8px', 
            lineHeight: 1 
          }}>
            {stats.total}
          </div>
          <div style={{ opacity: 0.9, fontSize: isMobile ? '14px' : '15px' }}>Total Users</div>
        </div>

        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 
          color: 'white',
          border: 'none',
          padding: isMobile ? '20px' : '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <UserCheck size={isMobile ? 24 : 28} style={{ opacity: 0.9 }} />
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '6px 12px', 
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}>
              Active
            </div>
          </div>
          <div style={{ 
            fontSize: isMobile ? '36px' : '42px', 
            fontWeight: '700', 
            marginBottom: '8px', 
            lineHeight: 1 
          }}>
            {stats.active}
          </div>
          <div style={{ opacity: 0.9, fontSize: isMobile ? '14px' : '15px' }}>Active Users</div>
        </div>

        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)', 
          color: 'white',
          border: 'none',
          padding: isMobile ? '20px' : '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <UserX size={isMobile ? 24 : 28} style={{ opacity: 0.9 }} />
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '6px 12px', 
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}>
              Inactive
            </div>
          </div>
          <div style={{ 
            fontSize: isMobile ? '36px' : '42px', 
            fontWeight: '700', 
            marginBottom: '8px', 
            lineHeight: 1 
          }}>
            {stats.inactive}
          </div>
          <div style={{ opacity: 0.9, fontSize: isMobile ? '14px' : '15px' }}>Inactive Users</div>
        </div>

        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
          color: 'white',
          border: 'none',
          padding: isMobile ? '20px' : '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <UserPlus size={isMobile ? 24 : 28} style={{ opacity: 0.9 }} />
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: '6px 12px', 
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}>
              New
            </div>
          </div>
          <div style={{ 
            fontSize: isMobile ? '36px' : '42px', 
            fontWeight: '700', 
            marginBottom: '8px', 
            lineHeight: 1 
          }}>
            {stats.newThisMonth}
          </div>
          <div style={{ opacity: 0.9, fontSize: isMobile ? '14px' : '15px' }}>New This Month</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card" style={{ padding: isMobile ? '20px' : '28px' }}>
        <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Filter size={isMobile ? 20 : 22} color="#667eea" />
            <h3 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '20px', 
              fontWeight: '600', 
              color: '#2c3e50' 
            }}>Search & Filters</h3>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginBottom: '20px', 
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            {/* Search */}
            <div style={{ flex: 1, minWidth: '280px', position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ marginBottom: 0, width: '100%', paddingLeft: '44px' }}
                />
                <Search 
                  size={20} 
                  color="#7f8c8d" 
                  style={{ 
                    position: 'absolute', 
                    left: '14px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }} 
                />
              </div>
            </div>

            {/* Filter Dropdown Button */}
            <div style={{ position: 'relative' }}>
              <button
                className="btn"
                style={{ 
                  padding: '12px 24px',
                  background: showFilters ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                  color: showFilters ? 'white' : '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.3s',
                  border: showFilters ? 'none' : '2px solid #e8ecf1',
                  fontWeight: '600'
                }}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={18} />
                <span>Filters</span>
                {(sortBy !== 'newest' || filterPeriod !== 'all' || filterStatus !== 'all' || startDate || endDate) && (
                  <span style={{
                    background: showFilters ? 'rgba(255,255,255,0.3)' : '#667eea',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '3px 10px',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}>
                    {[sortBy !== 'newest', filterPeriod !== 'all', filterStatus !== 'all', startDate, endDate].filter(Boolean).length}
                  </span>
                )}
              </button>

              {/* Filter Dropdown */}
              {showFilters && (
                <>
                  {/* Backdrop */}
                  <div 
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 999
                    }}
                    onClick={() => setShowFilters(false)}
                  />
                  
                  {/* Dropdown Panel */}
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '8px',
                    background: 'white',
                    border: '2px solid #e8ecf1',
                    borderRadius: '16px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    minWidth: '340px',
                    zIndex: 1000,
                    padding: '24px'
                  }}>
                    <h4 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>Filter Options</h4>
                    
                    {/* Sort By */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '10px', display: 'block', fontWeight: '600' }}>
                        Sort By
                      </label>
                      <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{ marginBottom: 0, width: '100%' }}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="name">Name (A-Z)</option>
                        <option value="email">Email (A-Z)</option>
                      </select>
                    </div>

                    {/* Period Filter */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '10px', display: 'block', fontWeight: '600' }}>
                        Time Period
                      </label>
                      <select 
                        value={filterPeriod} 
                        onChange={(e) => {
                          setFilterPeriod(e.target.value);
                          if (e.target.value !== 'custom') {
                            setStartDate('');
                            setEndDate('');
                          }
                        }}
                        style={{ marginBottom: 0, width: '100%' }}
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                        <option value="custom">Custom Date Range</option>
                      </select>
                    </div>

                    {/* Custom Date Range */}
                    {filterPeriod === 'custom' && (
                      <div style={{ marginBottom: '20px', padding: '16px', background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', borderRadius: '12px', border: '2px solid #66bb6a' }}>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ fontSize: '13px', color: '#2e7d32', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                            <Calendar size={14} />
                            <span>Start Date</span>
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ marginBottom: 0, width: '100%', fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#2e7d32', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                            <Calendar size={14} />
                            <span>End Date</span>
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            style={{ marginBottom: 0, width: '100%', fontSize: '14px' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Status Filter */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '10px', display: 'block', fontWeight: '600' }}>
                        User Status
                      </label>
                      <select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ marginBottom: 0, width: '100%' }}
                      >
                        <option value="all">All Users</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                      </select>
                    </div>

                    {/* Clear Filters Button */}
                    {(sortBy !== 'newest' || filterPeriod !== 'all' || filterStatus !== 'all' || startDate || endDate) && (
                      <button
                        className="btn"
                        style={{
                          width: '100%',
                          background: '#f8f9fa',
                          color: '#7f8c8d',
                          padding: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          border: '2px solid #e8ecf1'
                        }}
                        onClick={() => {
                          setSortBy('newest');
                          setFilterPeriod('all');
                          setFilterStatus('all');
                          setStartDate('');
                          setEndDate('');
                        }}
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Add User Button */}
            <button className="btn btn-primary" onClick={handleAdd} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
              <UserPlus size={18} />
              <span>Add User</span>
            </button>
          </div>

          {/* Filter Summary */}
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            flexWrap: 'wrap',
            padding: '16px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#495057',
            border: '1px solid #e8ecf1',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={16} color="#667eea" />
              <span style={{ fontWeight: '700', color: '#2c3e50' }}>Showing {sortedUsers.length} of {users.length} users</span>
            </div>
            {searchQuery && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Search size={14} color="#7f8c8d" />
                <span>Search: "{searchQuery}"</span>
              </div>
            )}
            {filterPeriod !== 'all' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} color="#7f8c8d" />
                {filterPeriod === 'custom' ? (
                  <span>
                    Date Range: {startDate ? new Date(startDate).toLocaleDateString() : 'Start'} - {endDate ? new Date(endDate).toLocaleDateString() : 'End'}
                  </span>
                ) : (
                  <span>Period: {filterPeriod}</span>
                )}
              </div>
            )}
            {filterStatus !== 'all' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {filterStatus === 'active' ? <CheckCircle size={14} color="#2e7d32" /> : <XCircle size={14} color="#c62828" />}
                <span>Status: {filterStatus}</span>
              </div>
            )}
            {sortBy !== 'newest' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Filter size={14} color="#7f8c8d" />
                <span>Sort: {sortBy}</span>
              </div>
            )}
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '18px', fontWeight: '500' }}>Loading users...</div>
          </div>
        ) : sortedUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '18px', fontWeight: '500' }}>
              {users.length === 0 ? 'No users found' : 'No users match the current filters'}
            </div>
          </div>
        ) : (
          <div style={{ 
            marginTop: '24px',
            position: 'relative'
          }}>
            <div style={{
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: '-ms-autohiding-scrollbar'
            }}>
            <table style={{ 
              width: '100%', 
              minWidth: isMobile ? '900px' : '100%',
              borderCollapse: 'separate', 
              borderSpacing: '0',
              background: 'white',
              borderRadius: '16px',
              overflow: 'visible',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
            }}>
              <thead>
                <tr style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>ID</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Name</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Email</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Password</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Status</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'left', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Created</th>
                  <th style={{ 
                    padding: '18px 20px', 
                    textAlign: 'center', 
                    fontWeight: '700', 
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid rgba(255,255,255,0.2)'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ position: 'relative' }}>
                {sortedUsers.map((user, index) => (
                  <tr 
                    key={user.id} 
                    style={{ 
                      background: index % 2 === 0 ? 'white' : '#fafbfc',
                      transition: 'all 0.3s ease',
                      cursor: 'default',
                      position: 'relative',
                      zIndex: openDropdown === user.id ? 10 : 1
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
                      e.currentTarget.style.transform = 'scale(1.01)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafbfc';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <td style={{ 
                      padding: '20px', 
                      fontWeight: '700', 
                      color: '#667eea',
                      fontSize: '15px',
                      borderBottom: '1px solid #e8ecf1'
                    }}>
                      #{user.id}
                    </td>
                    <td style={{ 
                      padding: '20px', 
                      fontWeight: '600', 
                      color: '#2c3e50',
                      fontSize: '15px',
                      borderBottom: '1px solid #e8ecf1'
                    }}>
                      {user.name}
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #e8ecf1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#7f8c8d', fontSize: '14px', fontWeight: '500' }}>{user.email}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(user.email);
                            success('Email copied to clipboard!');
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '8px',
                            transition: 'all 0.3s'
                          }}
                          title="Copy email"
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Copy size={14} color="#1976d2" />
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #e8ecf1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <code style={{ 
                          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#2e7d32',
                          fontFamily: 'monospace',
                          fontWeight: '700',
                          border: '1px solid #a5d6a7'
                        }}>
                          {user.plain_password || 'N/A'}
                        </code>
                        {user.plain_password && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(user.plain_password);
                              success('Password copied to clipboard!');
                            }}
                            style={{
                              background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: '8px',
                              transition: 'all 0.3s'
                            }}
                            title="Copy password"
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            <Copy size={14} color="#2e7d32" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #e8ecf1' }}>
                      <button
                        onClick={() => handleToggleStatus(user.id, user.is_active)}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '24px',
                          border: user.is_active ? '2px solid #66bb6a' : '2px solid #ef5350',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          background: user.is_active ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                          color: user.is_active ? '#2e7d32' : '#c62828',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.3s',
                          boxShadow: user.is_active ? '0 2px 8px rgba(102, 187, 106, 0.3)' : '0 2px 8px rgba(239, 83, 80, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.08)';
                          e.currentTarget.style.boxShadow = user.is_active ? '0 4px 12px rgba(102, 187, 106, 0.4)' : '0 4px 12px rgba(239, 83, 80, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = user.is_active ? '0 2px 8px rgba(102, 187, 106, 0.3)' : '0 2px 8px rgba(239, 83, 80, 0.3)';
                        }}
                      >
                        {user.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {user.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ 
                      padding: '20px', 
                      color: '#7f8c8d', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      borderBottom: '1px solid #e8ecf1'
                    }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ 
                      padding: '20px', 
                      borderBottom: '1px solid #e8ecf1',
                      position: 'relative',
                      overflow: 'visible'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        flexWrap: 'wrap',
                        position: 'relative'
                      }}>
                        {/* View Button */}
                        <button
                          className="btn"
                          style={{ 
                            padding: '8px 16px', 
                            fontSize: '14px', 
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
                            color: '#1565c0',
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            fontWeight: '600',
                            border: '2px solid #90caf9',
                            transition: 'all 0.3s'
                          }}
                          onClick={() => handleEdit(user)}
                          title="View user details"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <Eye size={16} />
                          <span>View</span>
                        </button>

                        {/* Copy All Button */}
                        <button
                          className="btn"
                          style={{ 
                            padding: '8px 16px', 
                            fontSize: '14px', 
                            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', 
                            color: '#6a1b9a',
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            fontWeight: '600',
                            border: '2px solid #ce93d8',
                            transition: 'all 0.3s'
                          }}
                          onClick={() => {
                            const credentials = `Email: ${user.email}\nPassword: ${user.plain_password || 'N/A'}`;
                            navigator.clipboard.writeText(credentials);
                            success('Credentials copied to clipboard!');
                          }}
                          title="Copy email and password"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(156, 39, 176, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <Copy size={16} />
                          <span>Copy All</span>
                        </button>
                        
                        {/* Dropdown Menu */}
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            className="btn"
                            style={{ 
                              padding: '8px 12px', 
                              fontSize: '14px', 
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                              color: '#495057',
                              display: 'flex',
                              alignItems: 'center',
                              border: '2px solid #e8ecf1'
                            }}
                            onClick={() => setOpenDropdown(openDropdown === user.id ? null : user.id)}
                            title="More actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {openDropdown === user.id && (
                            <>
                              {/* Backdrop to close dropdown */}
                              <div 
                                style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 100
                                }}
                                onClick={() => setOpenDropdown(null)}
                              />
                              
                              {/* Dropdown Menu */}
                              <div style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                marginTop: '8px',
                                background: 'white',
                                border: '2px solid #e8ecf1',
                                borderRadius: '12px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                minWidth: '180px',
                                zIndex: 101,
                                overflow: 'hidden'
                              }}>
                                <button
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    handleEdit(user);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '14px',
                                    color: '#2c3e50',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)';
                                    e.target.style.paddingLeft = '22px';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                    e.target.style.paddingLeft = '18px';
                                  }}
                                >
                                  <Edit size={16} color="#f57c00" />
                                  <span>Edit User</span>
                                </button>
                                
                                <button
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    handleResetPassword(user);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '14px',
                                    color: '#2c3e50',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)';
                                    e.target.style.paddingLeft = '22px';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                    e.target.style.paddingLeft = '18px';
                                  }}
                                >
                                  <Key size={16} color="#f57f17" />
                                  <span>Reset Password</span>
                                </button>
                                
                                <div style={{ height: '2px', background: 'linear-gradient(90deg, #e8ecf1 0%, #dee2e6 100%)', margin: '4px 0' }} />
                                
                                <button
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    handleDelete(user.id);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '14px',
                                    color: '#c62828',
                                    fontWeight: '600',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
                                    e.target.style.paddingLeft = '22px';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                    e.target.style.paddingLeft = '18px';
                                  }}
                                >
                                  <Trash2 size={16} />
                                  <span>Delete User</span>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <UserModal
          user={selectedUser}
          mode={modalMode}
          onClose={() => setShowModal(false)}
          onSave={fetchUsers}
        />
      )}

      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default AdminPanel;
